<div class="dashboard-container">
  <h3 class="step-title">Onboarding Status</h3>
  <p class="step-subtitle">Review your setup progress and complete any remaining steps.</p>

  <!-- Progress Summary -->
  <div class="progress-card">
    <div class="progress-header">
      <span class="progress-text">{{ completedCount }} of {{ totalSteps }} steps complete</span>
      <button mat-icon-button (click)="loadStatus()" [disabled]="isRefreshing" title="Refresh">
        <mat-spinner *ngIf="isRefreshing" diameter="18"></mat-spinner>
        <mat-icon *ngIf="!isRefreshing">refresh</mat-icon>
      </button>
    </div>
    <div class="progress-bar-bg">
      <div class="progress-bar-fill" [style.width.%]="(completedCount / totalSteps) * 100"></div>
    </div>
  </div>

  <!-- Step Checklist -->
  <div class="checklist">
    <div class="checklist-item" [class.done]="steps.businessDetails">
      <mat-icon>{{ steps.businessDetails ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
      <span>Business Details</span>
    </div>
    <div class="checklist-item" [class.done]="steps.representativeDetails">
      <mat-icon>{{ steps.representativeDetails ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
      <span>Representative Details</span>
    </div>
    <div class="checklist-item" [class.done]="steps.documents">
      <mat-icon>{{ steps.documents ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
      <span>Supporting Documents</span>
    </div>
    <div class="checklist-item" [class.done]="steps.stripeConnect">
      <mat-icon>{{ steps.stripeConnect ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
      <span>Stripe Connect</span>
      <span class="badge" *ngIf="!steps.stripeConnect">Required</span>
    </div>
    <div class="checklist-item" [class.done]="steps.brevo">
      <mat-icon>{{ steps.brevo ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
      <span>Brevo Email Setup</span>
      <span class="badge" *ngIf="!steps.brevo">Required</span>
    </div>
  </div>

  <mat-divider></mat-divider>

  <!-- Brevo Setup -->
  <div class="brevo-section">
    <div class="section-header">
      <img src="https://www.brevo.com/wp-content/themes/brevo-theme/assets/images/logo-brevo.svg"
           alt="Brevo" class="brevo-logo" onerror="this.style.display='none'">
      <div>
        <h4>Brevo Email Configuration</h4>
        <p *ngIf="!brevoConfigured">
          Connect your Brevo account to enable automated donor emails, receipts, and communications.
        </p>
        <p *ngIf="brevoConfigured" class="configured-msg">
          <mat-icon>check_circle</mat-icon>
          Brevo is configured and ready.
          <span *ngIf="brevoAccountEmail"> ({{ brevoAccountEmail }})</span>
        </p>
      </div>
    </div>

    <div *ngIf="!brevoConfigured" class="brevo-steps">
      <div class="brevo-step">
        <span class="step-num">1</span>
        <span>Sign up or log in at <a href="https://www.brevo.com" target="_blank">brevo.com</a></span>
      </div>
      <div class="brevo-step">
        <span class="step-num">2</span>
        <span>Go to <strong>Settings → API Keys</strong> and create a new key</span>
      </div>
      <div class="brevo-step">
        <span class="step-num">3</span>
        <span>Paste your API key below</span>
      </div>
    </div>

    <form *ngIf="!brevoConfigured" [formGroup]="brevoForm" (ngSubmit)="saveBrevoConfig()" class="brevo-form">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Brevo API Key</mat-label>
        <input
          matInput
          formControlName="apiKey"
          [type]="showApiKey ? 'text' : 'password'"
          placeholder="xkeysib-..."
        >
        <button mat-icon-button matSuffix type="button" (click)="showApiKey = !showApiKey">
          <mat-icon>{{ showApiKey ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <mat-error *ngIf="brevoForm.get('apiKey')?.hasError('required')">API key is required</mat-error>
      </mat-form-field>

      <div class="form-row two-col">
        <mat-form-field appearance="outline">
          <mat-label>Sender Name</mat-label>
          <input matInput formControlName="senderName" placeholder="Community Food Bank">
          <mat-error *ngIf="brevoForm.get('senderName')?.hasError('required')">Sender name required</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>From Email Address</mat-label>
          <input matInput formControlName="senderEmail" type="email" placeholder="hello@org.org">
          <mat-error *ngIf="brevoForm.get('senderEmail')?.hasError('required')">Sender email required</mat-error>
          <mat-error *ngIf="brevoForm.get('senderEmail')?.hasError('email')">Enter a valid email</mat-error>
        </mat-form-field>
      </div>

      <div class="form-actions">
        <button mat-raised-button color="primary" type="submit" [disabled]="brevoLoading">
          <mat-spinner *ngIf="brevoLoading" diameter="20" class="btn-spinner"></mat-spinner>
          <span>{{ brevoLoading ? 'Verifying...' : 'Connect Brevo' }}</span>
        </button>
      </div>
    </form>
  </div>

  <!-- All Complete Banner -->
  <div *ngIf="isComplete" class="complete-banner">
    <mat-icon>celebration</mat-icon>
    <div>
      <strong>Onboarding Complete!</strong>
      <p *ngIf="provisioningStatus !== 'complete'">
        Your workspace is being set up. You will receive an email with your console access link and password setup instructions once it is ready.
      </p>
      <p *ngIf="provisioningStatus === 'complete'">
        Your organization console is live! Check your email for the access link and password setup instructions.
      </p>
    </div>
  </div>

  <!-- Provisioning Status -->
  <div *ngIf="provisioningLabel" class="provisioning-status">
    <mat-icon *ngIf="provisioningStatus === 'complete'">check_circle</mat-icon>
    <mat-spinner *ngIf="provisioningStatus !== 'complete'" diameter="18"></mat-spinner>
    <span>{{ provisioningLabel }}</span>
  </div>

  <!-- Locked Notice -->
  <div *ngIf="isComplete" class="locked-notice">
    <mat-icon>lock</mat-icon>
    <p>Onboarding is complete. All further changes — including ordering new devices, managing deliveries, and updating settings — will be done from your <strong>Organization Console</strong>.</p>
  </div>
</div>
